import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Badge, Button, ButtonGroup, Card, CardBody, Col, Form, FormGroup,
  Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { isMtffProxyEnabled } from '../../mtff/mtffClient';
import {
  useCreatePageTheme,
  useDeletePageTheme,
  useMtffInstance,
  useMtffPageThemes,
  useUpdatePageTheme,
} from './useMtff';

/**
 * MTFF Page-Themes pro Instance.
 *
 * Spec: 02_applications/07_MTFF/theming.md §3 (Pattern-Matching), §4
 * (Admin-API), §8 (UX-Hinweise).
 *
 * Drei UI-Bereiche:
 *  1. Liste (sortiert sort_order ASC + route_pattern)
 *  2. Pattern-Tester — Editor tippt Pfad, UI zeigt welches Page-Theme gewinnt
 *  3. Editor-Modal (Create/Edit) mit Token-Diff-Preview + CSS-Linter
 */

const TOKEN_KEYS = [
  'color.brand', 'color.accent', 'color.surface', 'color.ink', 'color.muted',
  'font.body', 'font.heading', 'radius.card', 'layout.contentMax',
];

const BLOCKLIST = [
  /@import\b/i, /\bexpression\s*\(/i, /javascript\s*:/i, /\bbehavior\s*:/i,
  /url\s*\(\s*['"]?data:/i, /url\s*\(\s*['"]?javascript:/i,
];
const SAFE_SET = /^[a-zA-Z0-9#%(),.\- ]+$/;

/**
 * Pattern-Match-Algorithmus aus src/lib/theme/tokens.ts (MTFF).
 * Spezifität = Länge des Nicht-Wildcard-Präfix. Bei Gleichstand entscheidet
 * sortOrder ASC.
 */
function specificity(pattern, pathname) {
  if (pattern === '*') return 0;
  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -2);
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return prefix.length;
    return null;
  }
  if (pattern === pathname) return pattern.length + 1;
  return null;
}
function pickWinner(pageThemes, pathname) {
  const matches = pageThemes
    .map((row) => ({ row, s: specificity(row.routePattern, pathname) }))
    .filter((c) => c.s !== null);
  if (matches.length === 0) return null;
  matches.sort((a, b) => (b.s !== a.s ? b.s - a.s : (a.row.sortOrder ?? 0) - (b.row.sortOrder ?? 0)));
  return matches[0].row;
}

export function MtffPageThemesPage() {
  const { instanceId } = useParams();
  const enabled = isMtffProxyEnabled();
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const instance = useMtffInstance(instanceId);
  const query = useMtffPageThemes(instanceId);
  const del = useDeletePageTheme();

  const [editor, setEditor] = useState(null);
  const [testPath, setTestPath] = useState('/checkout');

  if (!enabled) {
    return (
      <div>
        <PageHeader title="MTFF · Page-Themes" />
        <Alert color="warning" className="small">
          MTFF-Admin-Proxy nicht konfiguriert.
        </Alert>
      </div>
    );
  }

  const list = query.data?.pageThemes ?? [];
  const winner = pickWinner(list, testPath);

  return (
    <div>
      <PageHeader
        title={`MTFF · Page-Themes · ${instance.data?.tenantId ?? '…'}/${instance.data?.instanceId ?? '…'}`}
        subtitle={`${list.length} Page-Themes · Pattern-Specifity matched über Längste-Präfix-Wins`}
        actions={
          <ButtonGroup size="sm">
            <Button color="secondary" outline tag={Link} to="/admin/settings/mtff/instances">
              ← Instances
            </Button>
            <HasPermission perms={['integrations.write']}>
              <Button color="primary" onClick={() => setEditor({ mode: 'create' })} disabled={!canWrite}>
                + Page-Theme
              </Button>
            </HasPermission>
          </ButtonGroup>
        }
      />

      <Row className="g-3">
        <Col lg="8">
          <DataTable
            loading={query.isLoading}
            error={query.error}
            empty={!query.isLoading && list.length === 0}
            emptyMessage="Noch keine Page-Themes."
            onRetry={() => query.refetch()}
          >
            <thead>
              <tr>
                <th style={{ width: 220 }}>Route-Pattern</th>
                <th>Label</th>
                <th style={{ width: 100 }}>Tokens</th>
                <th style={{ width: 100 }}>CSS</th>
                <th style={{ width: 90 }}>Order</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 130 }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className={winner?.id === p.id ? 'table-info' : ''}>
                  <td className="small"><code>{p.routePattern}</code></td>
                  <td className="small">{p.label ?? '—'}</td>
                  <td className="small">{Object.keys(p.tokens ?? {}).length}</td>
                  <td className="small">{p.customCss ? `${p.customCss.length} B` : '—'}</td>
                  <td className="small">{p.sortOrder ?? 0}</td>
                  <td>
                    <Badge color={p.isPublished ? 'success' : 'secondary'} pill>
                      {p.isPublished ? 'live' : 'draft'}
                    </Badge>
                  </td>
                  <td>
                    <HasPermission perms={['integrations.write']}>
                      <ButtonGroup size="sm">
                        <Button color="info" outline onClick={() => setEditor({ mode: 'edit', row: p })} disabled={!canWrite}>
                          Edit
                        </Button>
                        <Button
                          color="danger" outline
                          onClick={() => del.mutate({ instanceId, pageThemeId: p.id })}
                          disabled={!canWrite || del.isPending}
                        >
                          ✕
                        </Button>
                      </ButtonGroup>
                    </HasPermission>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Col>

        <Col lg="4">
          <Card>
            <CardBody>
              <h6 className="text-uppercase small text-muted mb-2">Pattern-Tester</h6>
              <FormGroup className="mb-2">
                <Label className="small">Pfad eintippen</Label>
                <Input
                  type="text"
                  bsSize="sm"
                  value={testPath}
                  onChange={(e) => setTestPath(e.target.value)}
                  placeholder="/checkout"
                />
              </FormGroup>
              {winner ? (
                <Alert color="info" className="small mb-0">
                  Gewinner: <code>{winner.routePattern}</code> ({winner.label ?? `#${winner.id}`})
                  {' '}<Badge color="info" pill>specificity {specificity(winner.routePattern, testPath)}</Badge>
                </Alert>
              ) : (
                <Alert color="warning" className="small mb-0">
                  Kein Pattern matched — Renderer fällt auf Instance-Theme (L1) zurück.
                </Alert>
              )}
              <hr />
              <p className="small text-muted mb-0">
                Pattern-Formen: <code>*</code>, <code>/checkout</code> (exakt),
                {' '}<code>/dashboard/*</code> (Präfix). Längstes Nicht-Wildcard-
                Präfix gewinnt; bei Gleichstand kleinerer <code>sortOrder</code>.
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {editor && (
        <PageThemeEditorModal
          key={editor.mode === 'edit' ? editor.row.id : 'new'}
          instanceId={instanceId}
          mode={editor.mode}
          row={editor.row}
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  );
}

function PageThemeEditorModal({ instanceId, mode, row, onClose }) {
  const create = useCreatePageTheme();
  const update = useUpdatePageTheme();
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(() => ({
    routePattern: row?.routePattern ?? '',
    label: row?.label ?? '',
    sortOrder: row?.sortOrder ?? 100,
    publish: row?.isPublished ?? false,
    tokens: { ...(row?.tokens ?? {}) },
    customCss: row?.customCss ?? '',
  }));

  const tokenValidationError = useMemo(() => {
    for (const k of TOKEN_KEYS) {
      const v = form.tokens[k];
      if (v && !SAFE_SET.test(v)) return `${k}: ungültiges Zeichen.`;
    }
    return null;
  }, [form.tokens]);

  const cssWarnings = useMemo(() => {
    if (!form.customCss) return [];
    return BLOCKLIST.filter((re) => re.test(form.customCss));
  }, [form.customCss]);

  const blocked = !!tokenValidationError || cssWarnings.length > 0 || !form.routePattern.trim();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (blocked) return;
    // Tokens säubern — leere Werte nicht senden, sonst überschreibt der Page-Theme
    // den Instance-Layer mit leeren Strings.
    const tokens = Object.fromEntries(
      Object.entries(form.tokens).filter(([, v]) => v && String(v).trim()),
    );
    const body = {
      routePattern: form.routePattern.trim(),
      label: form.label.trim() || undefined,
      sortOrder: Number(form.sortOrder) || 100,
      publish: !!form.publish,
      tokens,
      customCss: form.customCss || undefined,
    };
    if (isEdit) {
      await update.mutateAsync({ instanceId, pageThemeId: row.id, ...body });
    } else {
      await create.mutateAsync({ instanceId, ...body });
    }
    onClose();
  };

  const setToken = (k, v) => setForm((f) => ({ ...f, tokens: { ...f.tokens, [k]: v } }));

  return (
    <Modal isOpen toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {isEdit ? `Page-Theme #${row.id} bearbeiten` : 'Neues Page-Theme'}
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <Row className="g-3">
            <Col md="6">
              <FormGroup>
                <Label>Route-Pattern *</Label>
                <Input
                  type="text"
                  value={form.routePattern}
                  onChange={(e) => setForm((f) => ({ ...f, routePattern: e.target.value }))}
                  placeholder="/checkout oder /dashboard/* oder *"
                  required
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>Label</Label>
                <Input
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="Checkout-CTA"
                />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label>sortOrder</Label>
                <Input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                />
              </FormGroup>
            </Col>
          </Row>

          <h6 className="text-uppercase small text-muted mt-3 mb-2">Token-Overrides</h6>
          <p className="small text-muted">
            Nur Werte die du hier setzt überschreiben die Instance-Tokens auf
            matchenden Routen. Leere Felder fallen auf L1.
          </p>
          <Row className="g-2">
            {TOKEN_KEYS.map((k) => (
              <Col md="6" key={k}>
                <FormGroup className="mb-1">
                  <Label className="small">{k}</Label>
                  <Input
                    type="text"
                    bsSize="sm"
                    value={form.tokens[k] ?? ''}
                    onChange={(e) => setToken(k, e.target.value)}
                    pattern="[a-zA-Z0-9#%(),.\- ]*"
                  />
                </FormGroup>
              </Col>
            ))}
          </Row>
          {tokenValidationError && (
            <Alert color="danger" className="small mt-2 mb-0">{tokenValidationError}</Alert>
          )}

          <h6 className="text-uppercase small text-muted mt-3 mb-2">Page-Custom-CSS</h6>
          <p className="small text-muted">
            Ersetzt das Instance-Custom-CSS auf matchenden Routen (NICHT
            additiv). Leer lassen wenn nicht benötigt.
          </p>
          <Input
            type="textarea"
            value={form.customCss}
            onChange={(e) => setForm((f) => ({ ...f, customCss: e.target.value }))}
            rows={6}
            spellCheck={false}
            style={{ fontFamily: 'var(--bs-font-monospace)', fontSize: 13 }}
          />
          {cssWarnings.length > 0 && (
            <Alert color="danger" className="small mt-2 mb-0">
              CSS enthält geblockte Konstrukte — Backend würde mit 400 ablehnen.
            </Alert>
          )}

          <FormGroup check className="mt-3">
            <Input
              type="checkbox"
              id="pt-publish"
              checked={form.publish}
              onChange={(e) => setForm((f) => ({ ...f, publish: e.target.checked }))}
            />
            <Label check for="pt-publish">Direkt veröffentlichen (sonst Draft)</Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose}>Abbrechen</Button>
          <Button color="primary" type="submit" disabled={blocked || create.isPending || update.isPending}>
            {(create.isPending || update.isPending) ? 'Speichere…' : (isEdit ? 'Speichern' : 'Anlegen')}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
